import React, { useState } from "react";
import { Text, View } from "native-base";
import { Image } from "react-native";
import { Copy } from "lucide-react-native";
import { DeanteData } from "../../../Universal/API/Context-API/AppProvider";
import { useCart } from "../../../Universal/API/Context-API/CartContext";
import PressableWrap from "../../../Universal/Buttons/PressableColorWrap/PressableColorWrap";
import PressableWrapp from "../../../Universal/Buttons/PressableReact/PressableWrapp";
import OstatkiDisplay from "../../OstatkiDisplay/OstatkiDisplay";
import { ColorOption, SizeOption } from "./types";
import { UnitFullV2Styles } from "./styles";
import { normalizeText } from "./helpers";

const isBadgeSwatch = (swatch: ColorOption["swatch"]): swatch is { link: string } =>
  typeof swatch === "object" && swatch !== null && typeof swatch.link === "string";

interface UnitFullV2SummaryProps {
  available: boolean;
  colorOptions: ColorOption[];
  sizeOptions: SizeOption[];
  currentUnit: DeanteData;
  isSmallVersion: boolean;
  priceText: React.ReactNode;
  scaleAll: number;
  styles: UnitFullV2Styles;
  onCopyArt: (art: string) => void;
  onGoToCollection: (collectionName: string) => void;
  onSelectColorUnit: (unitId: string | number) => void;
  onSelectSizeUnit: (unitId: string | number) => void;
}

const UnitFullV2Summary: React.FC<UnitFullV2SummaryProps> = ({
  available,
  colorOptions,
  sizeOptions,
  currentUnit,
  isSmallVersion,
  priceText,
  scaleAll,
  styles,
  onCopyArt,
  onGoToCollection,
  onSelectColorUnit,
  onSelectSizeUnit,
}) => {
  const { addItem, decrementItem, getQuantity, incrementItem } = useCart();
  const [hoveredColorKey, setHoveredColorKey] = useState<string | null>(null);
  const quantity = getQuantity(currentUnit.id);
  const canSwitchColor = colorOptions.filter((option) => option.unitId !== undefined).length > 1;
  const canSwitchSize = sizeOptions.filter((option) => option.unitId !== undefined).length > 1;
  const activeColorLabel =
    colorOptions.find((option) => option.isActive)?.label ??
    colorOptions[0]?.label ??
    "";

  return (
    <View style={[styles.summaryCard, isSmallVersion ? styles.summaryCardSmall : null]}>
      {priceText ? (
        <View style={styles.priceBlock}>
          <Text style={styles.sectionEyebrow}>Стоимость</Text>
          <Text style={styles.priceValue}>{priceText}</Text>
        </View>
      ) : null}

      {colorOptions.length > 0 ? (
        <View style={styles.sectionBlock}>
          <View style={styles.sectionHeaderRow}>
            <Text style={styles.sectionEyebrow}>Отделка</Text>
            {activeColorLabel ? (
              <Text style={styles.sectionHeaderValue}>{activeColorLabel}</Text>
            ) : null}
          </View>
          <View style={styles.swatchRow}>
            {colorOptions.map((color) => (
              <PressableWrapp
                key={color.key}
                disabled={!canSwitchColor || color.unitId === undefined || color.isActive}
                onHoverIn={() => {
                  if (isSmallVersion) return;
                  setHoveredColorKey(color.key);
                }}
                onHoverOut={() => {
                  if (isSmallVersion) return;
                  setHoveredColorKey((prev) => (prev === color.key ? null : prev));
                }}
                onPress={() => {
                  if (color.unitId === undefined) return;
                  onSelectColorUnit(color.unitId);
                }}
                style={[
                  styles.swatchItem,
                  canSwitchColor ? styles.swatchItemPressable : null,
                  color.isActive ? styles.swatchItemActive : null,
                  isSmallVersion ? styles.swatchItemSmall : null,
                ]}
              >
                {hoveredColorKey === color.key ? (
                  <View pointerEvents="none" style={styles.swatchTooltipWrap}>
                    <View style={styles.swatchTooltip}>
                      <Text numberOfLines={1} style={styles.swatchTooltipText}>
                        {color.label}
                      </Text>
                    </View>
                  </View>
                ) : null}
                <View
                  style={[
                    styles.swatchCircle,
                    color.isActive ? styles.swatchCircleActive : null,
                    isSmallVersion ? styles.swatchCircleSmall : null,
                    {
                      backgroundColor: isBadgeSwatch(color.swatch) ? "#FFFFFF" : color.swatch,
                      borderColor:
                        color.isActive
                          ? "#111111"
                          : color.swatch === "#F8F8F6"
                            ? "#D6D2CB"
                            : "#1B1B1D",
                    },
                  ]}
                >
                  {isBadgeSwatch(color.swatch) ? (
                    <Image
                      source={{ uri: color.swatch.link }}
                      style={{ width: "100%", height: "100%", borderRadius: 999 }}
                      resizeMode="cover"
                    />
                  ) : null}
                </View>
              </PressableWrapp>
            ))}
          </View>
        </View>
      ) : null}

      {available ? (
        <View style={styles.sectionBlock}>
          <Text style={styles.sectionEyebrow}>Остатки</Text>
          <View style={styles.stockCardWrap}>
            <OstatkiDisplay unit={currentUnit} scaleAll={scaleAll} />
          </View>
        </View>
      ) : null}

      {sizeOptions.length > 0 ? (
        <View style={styles.sectionBlock}>
          <Text style={styles.sectionEyebrow}>Размеры</Text>
          <View style={styles.variantChipRow}>
            {sizeOptions.map((size) => (
              <PressableWrapp
                key={size.key}
                disabled={!canSwitchSize || size.unitId === undefined || size.isActive}
                onPress={() => {
                  if (size.unitId === undefined) return;
                  onSelectSizeUnit(size.unitId);
                }}
                style={[
                  styles.variantChip,
                  size.isActive ? styles.variantChipActive : null,
                  isSmallVersion ? styles.variantChipSmall : null,
                ]}
              >
                <Text
                  style={[
                    styles.variantChipLabel,
                    size.isActive ? styles.variantChipLabelActive : null,
                  ]}
                >
                  {size.label}
                </Text>
              </PressableWrapp>
            ))}
          </View>
        </View>
      ) : null}

      <View style={styles.actionColumn}>
        {quantity > 0 ? (
          <View style={styles.cartSummaryCard}>
            <View style={styles.cartSummaryMeta}>
              <Text selectable={false} style={styles.cartSummaryTitle}>Текущий заказ</Text>
              <Text selectable={false} style={styles.cartSummaryValue}>{quantity} шт.</Text>
            </View>
            <View style={styles.cartSummaryActions}>
              <PressableWrapp
                style={styles.cartSummaryGhostButton}
                onPress={() => decrementItem(currentUnit.id)}
              >
                <Text selectable={false} style={styles.cartSummaryGhostButtonText}>-</Text>
              </PressableWrapp>
              <PressableWrapp
                style={styles.cartSummaryPrimaryButton}
                onPress={() => incrementItem(currentUnit.id)}
              >
                <Text selectable={false} style={styles.cartSummaryPrimaryButtonText}>+</Text>
              </PressableWrapp>
            </View>
          </View>
        ) : (
          <PressableWrapp
            style={styles.cartSummaryAddButton}
            onPress={() => addItem(currentUnit.id, 1)}
          >
            <Text selectable={false} style={styles.cartSummaryAddButtonLabel}>Корзина</Text>
            <Text selectable={false} style={styles.cartSummaryAddButtonValue}>Добавить в заказ</Text>
          </PressableWrapp>
        )}

        {normalizeText(currentUnit.col) ? (
          <PressableWrap
            onPress={() => onGoToCollection(currentUnit.col)}
            style={styles.actionButtonPress}
          >
            <View style={styles.actionButton}>
              <Text selectable={false} style={styles.actionButtonLabel}>Открыть коллекцию</Text>
              <Text selectable={false} style={styles.actionButtonValue}>{currentUnit.col}</Text>
            </View>
          </PressableWrap>
        ) : null}

        {normalizeText(currentUnit.art) ? (
          <PressableWrapp
            style={styles.copyCard}
            onPress={() => onCopyArt(currentUnit.art)}
          >
            <View style={styles.copyIconBox}>
              <Copy size={16 * scaleAll} color="#101010" />
            </View>
            <View style={styles.copyTextWrap}>
              <Text style={styles.copyTitle}>Код продукта</Text>
              <Text style={styles.copyValue}>{currentUnit.art}</Text>
            </View>
          </PressableWrapp>
        ) : null}
      </View>
    </View>
  );
};

export default React.memo(UnitFullV2Summary);
